'use client';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  formatUsdFromCents,
  queryTomVendingInferredSales,
  backfillTomVendingInferredSales,
  type TomVendingInferredSaleRow,
} from '@/hooks/useTomVendingReports';
import { useAuthData } from '@/hooks/getAuthData';
import { useNotification } from '@/contexts/NotificationContext';
import { useTomVendingLocationsList, tomVendingLocationIsWarehouse } from '@/hooks/useTomVendingLocation';
import { formatVisitDateLabel } from '../toms-vending/TomsVendingSiteVisitDetailContent';
import { downloadInferredSalesXlsx, TOMS_REPORT_EXPORT_MAX_ROWS } from '@/utils/tomsVendingReportExport';
import { fetchAllReportRows } from '@/utils/downloadXlsx';

const PAGE_SIZE = 50;

function defaultDateRange() {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  };
}

type LocRow = { id: number; name?: string; isWarehouse?: boolean };

const TomsVendingInferredSalesReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin' || x.name === 'SystemAdmin');
  const { data: locations = [], isLoading: locLoading } = useTomVendingLocationsList();

  const init = useMemo(() => defaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState(init.dateFrom);
  const [dateTo, setDateTo] = useState(init.dateTo);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [data, setData] = useState<TomVendingInferredSaleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    totalUnitsSold: 0,
    totalRetailCents: 0,
    rowsMissingPrice: 0,
    rowsMissingCost: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [backfilling, setBackfilling] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await queryTomVendingInferredSales({
        dateFrom,
        dateTo,
        locationTomVendingId:
          locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId),
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      });
      setData(r.data);
      setTotal(r.total);
      setSummary(r.summary);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to load inferred sales');
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, locationId, pageIndex, showError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const handleDownloadExcel = useCallback(async () => {
    setExporting(true);
    try {
      const locationTomVendingId =
        locationId === '' || Number.isNaN(Number(locationId)) ? undefined : Number(locationId);
      const { rows } = await fetchAllReportRows({
        pageSize: 500,
        maxRows: TOMS_REPORT_EXPORT_MAX_ROWS,
        fetchPage: async (skip, take) => {
          const r = await queryTomVendingInferredSales({
            dateFrom,
            dateTo,
            locationTomVendingId,
            skip,
            take,
          });
          return { rows: r.data, total: r.total };
        },
      });
      if (rows.length === 0) {
        showError('Nothing to export for the selected date range.');
        return;
      }
      downloadInferredSalesXlsx(rows, dateFrom, dateTo);
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to download Excel');
    } finally {
      setExporting(false);
    }
  }, [dateFrom, dateTo, locationId, showError]);

  const locOpts = locations as LocRow[];
  const selectedLoc = locationId === '' ? null : locOpts.find((l) => l.id === locationId) ?? null;

  const columns = useMemo<MRT_ColumnDef<TomVendingInferredSaleRow>[]>(
    () => [
      {
        accessorKey: 'periodEndDate',
        header: 'Period end',
        Cell: ({ cell }) => formatVisitDateLabel(String(cell.getValue() ?? '')),
      },
      {
        id: 'periodStart',
        header: 'Period start',
        Cell: ({ row }) =>
          row.original.periodStartDate ? formatVisitDateLabel(row.original.periodStartDate) : '—',
      },
      { accessorKey: 'locationName', header: 'Location' },
      {
        id: 'machine',
        header: 'Machine',
        Cell: ({ row }) => row.original.assetId?.trim() || `#${row.original.vendingMachineId}`,
      },
      { accessorKey: 'upc', header: 'UPC', size: 120 },
      {
        accessorKey: 'itemName',
        header: 'Product',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      { accessorKey: 'unitsSold', header: 'Units', size: 72 },
      {
        accessorKey: 'unitPriceCents',
        header: 'Unit price',
        Cell: ({ row }) =>
          row.original.missingPrice ? (
            <Chip label="No price" size="small" color="warning" variant="outlined" />
          ) : (
            formatUsdFromCents(row.original.unitPriceCents)
          ),
      },
      {
        accessorKey: 'unitCostCents',
        header: 'Unit COGS',
        Cell: ({ row }) =>
          row.original.missingCost ? (
            <Chip label="No fill cost" size="small" color="warning" variant="outlined" />
          ) : (
            formatUsdFromCents(row.original.unitCostCents)
          ),
      },
      {
        accessorKey: 'retailCents',
        header: 'Retail',
        Cell: ({ row }) =>
          row.original.missingPrice ? (
            <Chip label="No price" size="small" color="warning" variant="outlined" />
          ) : (
            formatUsdFromCents(row.original.retailCents)
          ),
      },
      {
        accessorKey: 'marginCents',
        header: 'Margin',
        Cell: ({ row }) =>
          row.original.missingCost ? (
            <Chip label="—" size="small" variant="outlined" />
          ) : (
            formatUsdFromCents(row.original.marginCents)
          ),
      },
    ],
    [],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Reports', href: '/app/toms-vending/reports' },
    { label: 'Inferred sales' },
  ];

  return (
    <Box>
      <TitleBar breadcrumbs={breadcrumbs} title="Inferred sales" />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
          Units sold are inferred from consecutive site visit counts (prior ending minus arrival par).
          Retail uses the catalog sale price effective at visit finalize time (from price history when
          available). Margin uses fill-weighted FIFO unit cost from warehouse fills in the same period.
          Not machine meter data.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPageIndex(0);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPageIndex(0);
            }}
          />
          <Autocomplete<LocRow>
            size="small"
            sx={{ minWidth: 260 }}
            options={locOpts}
            loading={locLoading}
            value={selectedLoc}
            onChange={(_, v) => {
              setLocationId(v?.id ?? '');
              setPageIndex(0);
            }}
            getOptionLabel={(l) => l.name?.trim() || `Location ${l.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => <TextField {...params} label="Location (optional)" />}
          />
          <Button variant="contained" size="small" onClick={() => void fetchData()} disabled={isLoading}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleDownloadExcel()}
            disabled={isLoading || exporting || total === 0}
          >
            {exporting ? 'Downloading…' : 'Download Excel'}
          </Button>
          {hasAdminRole ? (
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              disabled={backfilling}
              onClick={() => {
                void (async () => {
                  setBackfilling(true);
                  try {
                    const r = await backfillTomVendingInferredSales({ dateFrom, dateTo });
                    showSuccess(`Rebuilt ${r.rows} rows from ${r.visits} visits`);
                    await fetchData();
                  } catch (e: unknown) {
                    showError(e instanceof Error ? e.message : 'Backfill failed');
                  } finally {
                    setBackfilling(false);
                  }
                })();
              }}
            >
              {backfilling ? 'Rebuilding…' : 'Rebuild sales data'}
            </Button>
          ) : null}
          <Button component={RouterLink} to="/app/toms-vending/reports" variant="text" size="small" sx={{ textTransform: 'none' }}>
            All reports
          </Button>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Total units: <strong>{summary.totalUnitsSold}</strong>
          {' · '}
          Total retail: <strong>{formatUsdFromCents(summary.totalRetailCents)}</strong>
          {summary.rowsMissingPrice > 0 ? (
            <>
              {' · '}
              <Chip label={`${summary.rowsMissingPrice} rows missing price`} size="small" color="warning" />
            </>
          ) : null}
          {summary.rowsMissingCost > 0 ? (
            <>
              {' · '}
              <Chip label={`${summary.rowsMissingCost} rows missing COGS`} size="small" color="warning" />
            </>
          ) : null}
        </Typography>

        <MaterialReactTable
          columns={columns}
          data={data}
          manualPagination
          rowCount={total}
          enableGlobalFilter={false}
          onPaginationChange={(updater) => {
            setPageIndex(
              typeof updater === 'function'
                ? updater({ pageIndex, pageSize: PAGE_SIZE }).pageIndex
                : updater.pageIndex,
            );
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () =>
              navigate(
                `/app/toms-vending/site-visits/${row.original.curSiteVisitId}?locationId=${row.original.locationTomVendingId}`,
              ),
            sx: { cursor: 'pointer' },
          })}
          state={{ isLoading, pagination: { pageIndex, pageSize: PAGE_SIZE } }}
          initialState={{ density: 'compact' }}
        />
      </Box>
    </Box>
  );
};

export default TomsVendingInferredSalesReportPage;
